import type { LegalPolicy } from './legal.content';

/**
 * Vendor-facing policies (Part B of the business's legal pack, supplied 2026-09-21).
 * Shown on /vendor/profile and on the public /legal page's "For vendors" tab.
 * The wording is the business's own — square-bracketed values are theirs to finalise.
 */
export const VENDOR_POLICIES: readonly LegalPolicy[] = [
  {
    key: 'terms',
    title: 'Terms and Conditions',
    sub: 'The agreement between your business and Trexa',
    blocks: [
      { k: 'h', t: '1. About these Terms' },
      { k: 'p', t: 'These Vendor Terms govern the use of Trexa by venues, halls and service providers ("Vendor", "you") and are an agreement between you and Inspiration Valley Enterprise (SSM: 002746500-T) ("Trexa"). They apply together with the Vendor Privacy Policy, Vendor Payout & Refund Policy, and any fee schedule agreed in writing.' },

      { k: 'h', t: '2. Vendor roles' },
      { k: 'li', t: "Venue (Lead Vendor): a hall or venue that creates bookings, sets the customer price, and may include partner Vendors' services in its packages." },
      { k: 'li', t: "Partner Vendor: a caterer, photographer, makeup artist, decorator or other provider whose services are included in a Venue's booking." },
      { k: 'p', t: 'A business may act in both roles.' },

      { k: 'h', t: '3. Eligibility and verification' },
      { k: 'li', m: 'a)', t: 'You must be a business registered in Malaysia (SSM or equivalent) and provide valid registration documents, director/owner identification, and a Malaysian bank account in the business name.' },
      { k: 'li', m: 'b)', t: 'Your account is activated for payouts only after verification is complete.' },
      { k: 'li', m: 'c)', t: 'You must keep your information up to date. We may request further documents at any time to meet regulatory and anti-money-laundering requirements.' },

      { k: 'h', t: '4. Your listings and bookings' },
      { k: 'li', m: 'a)', t: 'You are responsible for the accuracy of your packages, prices, availability and terms.' },
      { k: 'li', m: 'b)', t: "Once you accept a booking and the customer's deposit is received, you must hold the date and deliver the services as described." },
      { k: 'li', m: 'c)', t: 'All payments for bookings made through Trexa must be collected through Trexa. Collecting payment outside the platform for a Trexa booking is a breach of these Terms.' },

      { k: 'h', t: '5. Partner arrangements' },
      { k: 'li', m: 'a)', t: "A Venue may add a Partner Vendor's services to a booking only after the Partner has agreed to the relevant package." },
      { k: 'li', m: 'b)', t: "The Venue sets the price charged to the customer. Partner Vendors see only their own agreed amounts and will not be shown the Venue's margin." },
      { k: 'li', m: 'c)', t: "Partners must approve or decline their portion of a booking within [X] days. A Partner who withdraws after agreeing must notify the Venue and Trexa immediately. The Venue must then arrange a substitute (at the customer's originally quoted price, with customer consent) or remove that portion." },

      { k: 'h', t: '6. Fees' },
      { k: 'li', m: 'a)', t: 'Platform Fee: Trexa charges a platform fee on each release to you. The default rate is [4]% of the released amount, unless a different rate is agreed with you in writing.' },
      { k: 'li', m: 'b)', t: 'The fee rate applicable to a booking is fixed when the invoice is sent to the customer. Later changes to your fee rate do not affect bookings already invoiced.' },
      { k: 'li', m: 'c)', t: 'The Customer Protection Fee is paid by the customer on top of the order total and is not deducted from your earnings.' },
      { k: 'li', m: 'd)', t: 'Fees are deducted automatically from payouts. Applicable taxes (e.g. SST) will be charged as required by law.' },

      { k: 'h', t: '7. Custody and release of funds' },
      { k: 'li', m: 'a)', t: "Customer payments are held through Razorpay Curlec's marketplace settlement facility and are not your property until released." },
      { k: 'li', m: 'b)', t: 'Funds are released according to the milestone schedule on the invoice, and in full after the customer confirms completion or the confirmation window lapses without a dispute.' },
      { k: 'li', m: 'c)', t: "For multi-vendor bookings, each Vendor's share is released directly according to the agreed split." },

      { k: 'h', t: '8. Early release and preparation advance (optional)' },
      { k: 'li', m: 'a)', t: 'Eligible Vendors may request an early release or a preparation advance before the event, subject to limits based on your booking history and volume, per-booking caps and rolling limits set by Trexa.' },
      { k: 'li', m: 'b)', t: 'If a booking is cancelled, any early release or advance paid to you must be repaid. Trexa may recover it from your forfeit share or future payouts.' },
      { k: 'li', m: 'c)', t: 'Trexa may suspend early release and advance access at any time.' },

      { k: 'h', t: '9. Cancellations' },
      { k: 'li', m: 'a)', t: 'Customer cancels: handled under the customer Refund Policy. Where a deposit is forfeited, the forfeit is paid to you (less platform fee) or to the Trexa guarantee fund, as set by platform policy at the time of booking.' },
      { k: 'li', m: 'b)', t: 'You cancel: the customer receives a full refund of everything paid towards the order; the Customer Protection Fee is not refunded. Trexa may charge you a cancellation charge of [RM X / X%], recover any early release or advance, and suspend your account for repeated cancellations.' },

      { k: 'h', t: '10. Disputes' },
      { k: 'li', m: 'a)', t: 'If a customer raises a dispute, the disputed amount remains in custody.' },
      { k: 'li', m: 'b)', t: 'You must respond with evidence within [5] working days. Trexa will decide on the release or refund of the held funds based on the information from both sides.' },
      { k: 'li', m: 'c)', t: "Trexa's decision covers only the funds held on the platform and does not limit either party's legal rights." },

      { k: 'h', t: '11. Conduct' },
      { k: 'p', t: 'You must not: misrepresent your services, harass customers, manipulate reviews, attempt to take bookings off-platform, or use the platform for any unlawful purpose.' },

      { k: 'h', t: '12. Liability and indemnity' },
      { k: 'li', m: 'a)', t: 'You are solely responsible for delivering your services and for complying with all licences, permits, food safety, and venue safety laws.' },
      { k: 'li', m: 'b)', t: 'You agree to indemnify Trexa against claims arising from your services, your breach of these Terms, or your violation of law.' },
      { k: 'li', m: 'c)', t: "Trexa's total liability to you is limited to the platform fees paid by you in the [3] months before the claim." },

      { k: 'h', t: '13. Suspension and termination' },
      { k: 'p', t: "Either party may end this agreement with [30] days' written notice. Trexa may suspend or terminate immediately for fraud, serious breach, or regulatory reasons. Bookings already confirmed must still be honoured, or refunded under Clause 9(b)." },

      { k: 'h', t: '14. Changes' },
      { k: 'p', t: "Trexa may update these Terms with [14] days' notice. Continued use after the effective date means acceptance. Changes do not reprice bookings already invoiced." },

      { k: 'h', t: '15. Governing law' },
      { k: 'p', t: 'These Terms are governed by the laws of Malaysia and subject to the jurisdiction of the courts of Kuala Lumpur.' },
    ],
  },

  {
    key: 'privacy',
    title: 'Privacy Policy',
    sub: 'How we handle your business data',
    blocks: [
      { k: 'p', t: 'This notice explains how Inspiration Valley Enterprise ("Trexa") handles the personal data of Vendors, their owners, directors and staff, in line with the PDPA 2010.' },

      { k: 'h', t: '1. Data we collect' },
      { k: 'li', t: "Business & identity: business name, SSM registration, owners' and directors' names, IC/passport numbers, addresses, contact details." },
      { k: 'li', t: 'Verification documents: SSM documents, bank statements or account verification letters.' },
      { k: 'li', t: 'Banking: bank name, account number and holder name for payouts.' },
      { k: 'li', t: 'Operational data: listings, packages, prices, bookings, partner arrangements, messages, payout and fee history.' },
      { k: 'li', t: 'Staff users: names, emails, phone numbers and activity logs of staff you add to your account.' },

      { k: 'h', t: '2. Why we use it' },
      { k: 'li', t: 'To verify your business (KYC) and meet anti-money-laundering and payment regulations.' },
      { k: 'li', t: 'To list your services and manage bookings.' },
      { k: 'li', t: 'To calculate fees and process payouts.' },
      { k: 'li', t: 'To handle disputes, prevent fraud, and enforce our Vendor Terms.' },
      { k: 'li', t: 'To send service updates and, with consent, product and marketing news.' },

      { k: 'h', t: '3. Who we share it with' },
      { k: 'li', t: 'Customers: your business name, listing details and contact information relevant to their booking.' },
      { k: 'li', t: 'Partner Vendors / Venues: the details needed to coordinate a shared booking. Venue pricing and margins are not shared with Partner Vendors.' },
      { k: 'li', t: 'Razorpay Curlec and banks: to verify accounts and send payouts.' },
      { k: 'li', t: 'Service providers and authorities: as described in our customer Privacy Policy.' },

      { k: 'h', t: '4. Retention' },
      { k: 'p', t: 'Verification, payout and transaction records are kept for [7] years after your account closes, as required for accounting and regulatory purposes.' },

      { k: 'h', t: '5. Security, breach notification, cross-border transfer and your rights' },
      { k: 'p', t: 'These are handled the same way as described in Sections 4, 6 and 7 of our customer Privacy Policy. Requests go to our Data Protection Officer at support@ivalley.my.' },

      { k: 'h', t: '6. Your responsibility' },
      { k: 'p', t: 'You must handle any customer personal data you receive through Trexa in compliance with the PDPA, and use it only to deliver the booked services.' },
    ],
  },

  {
    key: 'refund',
    title: 'Payout & Refund Policy',
    sub: 'When you get paid, and what happens on a cancellation',
    blocks: [
      { k: 'h', t: '1. When you get paid' },
      {
        k: 'tier',
        t: 'Milestones (if set on the invoice)',
        rows: [["What's released", 'The milestone amount, less platform fee']],
      },
      {
        k: 'tier',
        t: 'Event completed and confirmed by customer (or no dispute after [X] days)',
        rows: [["What's released", 'Remaining balance, less platform fee']],
      },
      {
        k: 'tier',
        t: 'Early release / preparation advance (if eligible)',
        rows: [["What's released", 'Advance amount, recovered at final release']],
      },
      { k: 'p', t: 'Payouts are sent to your verified business bank account within [2–3] working days of release.' },

      { k: 'h', t: '2. When a customer cancels' },
      { k: 'li', t: 'More than [3] days before the event: the customer is refunded. You receive nothing from the deposit, and any early release or advance must be repaid.' },
      { k: 'li', t: 'Within [3] days, or balance unpaid by [1] day before the event: the deposit is forfeited. The forfeit goes to you (split by share for multi-vendor bookings, less platform fee) or to the Trexa guarantee fund, according to the policy set when the booking was invoiced.' },

      { k: 'h', t: '3. When you cancel' },
      { k: 'p', t: 'The customer gets a full refund. Any early release or advance is clawed back from future payouts or must be repaid directly within [14] days. A cancellation charge of [RM X / X%] may apply.' },

      { k: 'h', t: '4. When a partner withdraws' },
      { k: 'p', t: 'The Venue must offer the customer a substitute at the original price or refund that portion. The withdrawing Partner receives no payout for that portion and must repay any advance received for it.' },

      { k: 'h', t: '5. Disputes' },
      { k: 'p', t: 'Disputed amounts are held until resolved. Undisputed portions are released as normal.' },

      { k: 'h', t: '6. Fee refunds' },
      { k: 'p', t: 'Platform fees are refunded to you only where the underlying release is reversed (e.g. a refund ordered after a dispute).' },

      { k: 'h', t: '7. Payout issues' },
      { k: 'p', t: 'Contact support@ivalley.my with the booking ID. Incorrect bank details supplied by you may delay payouts; Trexa is not liable for delays caused by incorrect information.' },
    ],
  },

  {
    key: 'about',
    title: 'About Us',
    sub: 'Get paid with confidence. Win the trust of every couple.',
    blocks: [
      { k: 'p', t: 'Couples in Malaysia are nervous about paying big wedding deposits, and for good reason. Every time a vendor disappears with deposits, it gets harder for honest businesses like yours to close bookings.' },
      { k: 'p', t: "Trexa is the deposit trust layer for wedding vendors. Customer payments go into protected custody, and you get paid on clear, fair milestones. Couples book with confidence; you get committed bookings and guaranteed payment for work you've delivered." },

      { k: 'h', t: 'Built for venues and their partners' },
      { k: 'li', t: 'One order, many vendors — halls can bundle catering, photography, makeup and décor into one invoice. Each partner is paid their own share directly.' },
      { k: 'li', t: 'Your margins stay private — partners see only their own amounts.' },
      { k: 'li', t: 'Deposits that stick — clear forfeit rules protect you from last-minute cancellations.' },
      { k: 'li', t: 'Cash flow support — eligible vendors can access early release or preparation advances.' },
      { k: 'li', t: 'Simple fees — a flat platform fee on released amounts. No monthly subscription.' },

      { k: 'p', t: 'Built in Kuala Lumpur by Inspiration Valley Enterprise, starting with wedding halls across the Klang Valley.' },
    ],
  },

  {
    key: 'contact',
    title: 'Contact Us',
    sub: 'Payouts, verification and partnerships',
    blocks: [
      { k: 'h', t: 'Want to join Trexa?' },
      { k: 'p', t: 'Talk to our partnerships team to set up your venue or vendor account.' },
      { k: 'li', t: 'Email: support@ivalley.my' },
      { k: 'li', t: 'WhatsApp: +60 18-703 5797' },
      { k: 'li', t: 'Phone: +60 18-703 5797' },

      { k: 'h', t: 'Existing vendors' },
      { k: 'li', t: 'Payouts, fees and account verification: support@ivalley.my' },
      { k: 'li', t: 'Booking disputes: use "Respond" on the dispute in your dashboard' },
      { k: 'li', t: 'Hours: Monday–Friday, 9:00am–6:00pm' },

      { k: 'h', t: 'Business address' },
      { k: 'p', t: 'Inspiration Valley Enterprise (SSM: 002746500-T)', tight: true },
      { k: 'p', t: 'Plaza Damas 1, Taman Sri Hartamas, 50480 Kuala Lumpur' },
    ],
  },
];
